import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  Input,
  isDevMode,
  output,
  signal,
  ViewChild
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { Client, E1EquipmentDto, FacilityOwnerDto } from '@fdm/api-client';
import { first, map, of, switchMap } from 'rxjs';
import { AddEquipmentsDialogComponent } from '../add-equipments-dialog/add-equipments-dialog.component';
import { PolicyService } from '../../../../../../services/roles/policy.service';
import { FilterResponse, TableColumnFilterComponent } from '@fdm/shared';
import { filterTable } from '@fdm/utils';

const DEFAULT_PAGES = [25];

export interface E1TableChange {
  equipments: E1EquipmentDto[];
  facilityOwner?: FacilityOwnerDto;
}

@Component({
  selector: 'app-e1-table',
  imports: [
    CommonModule,
    RouterModule,

    // Material
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatPaginatorModule,
    MatCheckboxModule,

    // Components
    TableColumnFilterComponent
  ],
  templateUrl: './e1-table.component.html',
  styleUrl: './e1-table.component.scss',
})
export class E1TableComponent implements AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;

  changed = output<E1TableChange>();

  @Input() required = false;

  @Input()
  public set values(items: E1EquipmentDto[]) {
    this._values = items;
    this.dataSource.data = items;
    this.tempTableData = [...this.dataSource.data];
  }

  public get values() {
    return this._values;
  }

  private _values: E1EquipmentDto[] = [];

  private _dialog = inject(MatDialog);
  private _policy = inject(PolicyService);
  readonly apiClient = inject(Client);

  // canEdit = signal(false);
  canEdit$ = this._policy.canEditTable$;

  displayedColumns: string[] = [
    'id',
    'equipmentTag',
    'description',
    'equipmentClass',
    'equipmentSubclass',
    'integrityCriticality',
    'assetConsequence',
    'facility',
    'area',
    'unit',
    'equipmentStatus',
  ];

  dataSource = new MatTableDataSource<E1EquipmentDto>();

  selection = new SelectionModel<E1EquipmentDto>(true, []);

  get showDelete() {
    return this.selection.selected?.length > 0;
  }

  facilityOwners = signal<FacilityOwnerDto[]>([]);

  pageSizeOptions = DEFAULT_PAGES;

  isDev = false;

  constructor() {
    this.isDev = isDevMode();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.dataSource.data.forEach((row) => {
        this.selection.select(row);
      });
    }
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected > 0 && numSelected === numRows;
  }

  checkRow(row: E1EquipmentDto) {
    this.selection.toggle(row);
  }

  addEquipment(): void {
    const currentFacility = this.dataSource.data?.[0]?.facility;

    this._dialog
      .open<AddEquipmentsDialogComponent, any, E1EquipmentDto[]>(
        AddEquipmentsDialogComponent,
        {
          width: '400px',
          height: '100vh',
          position: { right: '0' },
          data: {
            selectedItems: this.dataSource.data ?? [],
            source: 'E1',
            reportFacility: currentFacility,
          },
        }
      )
      .afterClosed()
      .pipe(first())
      .subscribe((data) => {
        if (data) {
          const values = [...this.dataSource.data];
          const sortedValues = data.filter((item) => {
            return values.length ? !values.some((val) => val.id === item.id) : true;
          });

          this.dataSource.data = [...values, ...sortedValues];
          this.tempTableData = [...this.dataSource.data];

          console.log(data);
          console.log(this.dataSource.data);

          this.changed.emit({ equipments: this.dataSource.data });
        }
      });
  }

  refresh() {
    this.dataSource.data = [...this.values];
    this.tempTableData = [...this.dataSource.data];
  }

  deleteEquipment() {
    const selectedItems = this.selection.selected as E1EquipmentDto[];
    const equipments = this.dataSource.data.filter(item => !selectedItems.some(selected => selected.id === item.id))

    this.dataSource.data = equipments;
    this.tempTableData = [...this.dataSource.data];
    this.changed.emit({ equipments });
    this.selection.clear();
  }

  export() {}
  
  filters: Record<string, FilterResponse | null> = {};
  tempTableData: any[] = [];

  applyFilter(res: FilterResponse | null, column: string) {
    this.filters[column] = res;
    this.dataSource.data = filterTable(this.tempTableData, column, res);
  }

  isActive(column: string) {    
    return this.filters[column] != null;
  }
}
